import React, { useEffect, useState } from 'react';
import warningIcon from '../../../assets/icons/hazard-icon.png';
import axios from 'axios';
import Spinner from '../../Spinner';

export default function StateAlerts(props) {
    const {
        fire,
        stateAlerts,
        setStateAlerts
    } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [search, setSearch] = useState('');

    const keywords = ['fire', 'burn', 'wind', 'smoke', 'wildfire', 'heat', 'evacuate', 'flames', 'blaze'];

    const getStateCode = async (lat, lon) => {
        setLoading(true);
        setError(false);
        const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${accessToken}`;
    
        try {
            const response = await fetch(url);
            const data = await response.json();
    
            if (data.features && data.features.length > 0) {
                const stateFeature = data.features.find(feature =>
                    feature.place_type.includes("region")
                );
    
                if (stateFeature) {
                    const stateCode = stateFeature.properties.short_code.replace("US-", "");
                    return stateCode.toUpperCase();
                } else {
                    setLoading(false);
                    setError('There are no state alerts currently available.');
                    throw new Error("State code not found in response.");
                }
            } else {
                setLoading(false);
                throw new Error("No results found for the given coordinates.");
            }
        } catch (error) {
            setLoading(false);
            setError('There are no state alerts currently available.');
            console.error("Failed to fetch state code from Mapbox:", error);
            return null;
        }
    }
    
    const getStateAlerts = () => {
        getStateCode(fire.latitude, fire.longitude)
        .then((stateCode) => {
            axios.get(`https://api.weather.gov/alerts/active?area=${stateCode}`)
            .then((res) => {
                setLoading(false);
                setStateAlerts(res.data.features
                    .filter((alert) => {
                    const description = alert.properties.description?.toLowerCase() || '';
                    const headline = alert.properties.headline?.toLowerCase() || '';
                    const instruction = alert.properties.instruction?.toLowerCase() || '';
                
                    // Check if any keyword is found in the description, headline, or instruction
                    return keywords.some((keyword) => 
                        description.includes(keyword) || 
                        headline.includes(keyword) || 
                        instruction.includes(keyword)
                    );
                }));
            }).catch((err) => {
                setLoading(false);
                setError('There are no state alerts currently available.');
                console.log('error getting disaster alerts', err);
            })
        })
    }

    useEffect(() => {
        getStateAlerts();
    }, []);

    const formatStateAlertTime = (isoDateString) => {
        const inputDate = new Date(isoDateString);
        const now = new Date();
        const diff = now - inputDate; // Difference in milliseconds
    
        if (isNaN(diff)) {
            return "Invalid date";
        }
    
        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const months = Math.floor(days / 30);
        const years = Math.floor(days / 365);
    
        if (seconds < 60) {
            return seconds === 1 ? "1 second ago" : `${seconds} seconds ago`;
        } else if (minutes < 60) {
            return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
        } else if (hours < 24) {
            return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
        } else if (days < 30) {
            return days === 1 ? "1 day ago" : `${days} days ago`;
        } else if (months < 12) {
            return months === 1 ? "1 month ago" : `${months} months ago`;
        } else {
            return years === 1 ? "1 year ago" : `${years} years ago`;
        }
    }

    return (
        <div className='fire-popup-state-alerts'>
            {loading ?
            <Spinner />
            :
            <>
            <div style={{ fontSize: '2rem' , color: 'black' }}>State Alerts</div>
            <div style={{ fontSize: '0.8rem' , color: 'black', marginBottom: '1rem' }}>Fire-related emergency warnings issued by the U.S. state where the fire is located. These alerts, such as Red Flag Warnings and Fire Weather Watches, are sourced directly from government agencies and provide critical, time-sensitive information about hazardous conditions that may increase wildfire risk, including lightning, dry fuels, and high winds.</div>
           {stateAlerts && stateAlerts.length > 0 && <div style={{ width: '100%', marginBottom: '1rem' }}>
           <div className="all-fires-search">
                <input
                type="text"
                className="all-fires-search-input"
                placeholder="Search key word"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                />
            </div>
            {stateAlerts && stateAlerts.length > 0 && <div className='fire-popup-state-alerts-length'>State Alerts ({stateAlerts.length})</div>}
            </div>}
            {(stateAlerts && stateAlerts.length === 0) || error ?
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: '2rem',
                textAlign: 'center',
                color: '#666'
            }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {error ? 'Unable to load state alerts' : 'No state alerts available'}
                </div>
                <div style={{ fontSize: '0.9rem' }}>
                    {error ? 'Please try again later or check your connection' : 'Check back later for new alerts or try a different location'}
                </div>
            </div>
            :
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {(() => {
                    const filteredAlerts = stateAlerts && stateAlerts.filter((alert) => {
                        const descriptionMatch = alert.properties.description?.toLowerCase().includes(search.toLowerCase());
                        const headlineMatch = alert.properties.headline?.toLowerCase().includes(search.toLowerCase());
                        const instructionMatch = alert.properties.instruction?.toLowerCase().includes(search.toLowerCase());
                        return descriptionMatch || headlineMatch || instructionMatch;
                    });
                    
                    return filteredAlerts && filteredAlerts.length > 0 ? (
                        <div style={{ maxHeight: '35vh', overflowY: 'auto', paddingBottom: '1rem' }}>
                            {filteredAlerts.map((alert, idx) => {
                    const highlightText = (text = '', searchTerm = '') => {
                        if (!searchTerm) return text;
                        const regex = new RegExp(`(${searchTerm})`, 'gi');
                        const parts = text.split(regex);
                        return parts.map((part, index) =>
                            part.toLowerCase() === searchTerm.toLowerCase() ? (
                                <span key={index} style={{ backgroundColor: 'yellow', color: 'black' }}>{part}</span>
                            ) : (
                                part
                            )
                        );
                    };

                    return (
                        <div 
                            className='fire-popup-state-alerts-card' 
                            key={alert.id}
                            style={{ 
                                marginBottom: idx === filteredAlerts.length - 1 ? '1rem' : '0'
                            }}
                        >
                            <div className='fire-popup-state-alerts-card-headline'>
                                <img src={warningIcon} className='fire-popup-state-alerts-card-headline-icon' />
                                <div className='fire-popup-state-alerts-card-headline-title'>
                                    {alert.properties.headline
                                        ? highlightText(alert.properties.headline, search)
                                        : 'State Alert'}
                                </div>
                            </div>
                            <div className='fire-popup-state-alerts-card-timestamp'>
                                {alert.properties.sent ? formatStateAlertTime(alert.properties.sent) : 'Time not available'}
                            </div>
                            <div className='fire-popup-state-alerts-card-description'>
                                {alert.properties.description
                                    ? highlightText(alert.properties.description, search)
                                    : 'No description.'}
                            </div>
                            <div className='fire-popup-state-alerts-card-instruction'>
                                <div className='fire-popup-state-alerts-card-instruction-title'>Instructions:</div>
                                <div className='fire-popup-state-alerts-card-instruction-value'>
                                    {alert.properties.instruction
                                        ? highlightText(alert.properties.instruction, search)
                                        : 'None'}
                                </div>
                            </div>
                        </div>
                    );
                            })}
                        </div>
                    ) : filteredAlerts && filteredAlerts.length === 0 ? (
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#666'
                        }}>
                            <div style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                No alerts match your search
                            </div>
                            <div style={{ fontSize: '0.9rem' }}>
                                Try adjusting your search criteria or check back later
                            </div>
                        </div>
                    ) : null;
                })()}
            </div>}
            </>}
        </div>
    )
}